import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { defaultMsalScopes } from "../AuthConfig";
import { msalInstance, msalInstanceReady } from "../msalInstance";
import {
  getCachedAccessToken,
  setCachedAccessToken,
  clearCachedAccessToken,
} from "./tokenStorage";

const FALLBACK_SCOPES = defaultMsalScopes.length
  ? defaultMsalScopes
  : ["User.Read"];

const buildRequestScopes = (scopes?: string[]) => {
  if (scopes && scopes.length) {
    return scopes;
  }
  return FALLBACK_SCOPES;
};

/**
 * Force re-authentication when token refresh fails.
 * Uses loginRedirect for seamless re-authentication.
 */
const forceRelogin = async (reason: string, accountHint?: string) => {
  console.log(`Token refresh failed (${reason}) - triggering seamless re-login`);
  clearCachedAccessToken();

  try {
    msalInstance.setActiveAccount(null);
    await msalInstance.clearCache();
  } catch (cacheErr) {
    console.warn("Failed to clear MSAL cache", cacheErr);
  }

  try {
    await msalInstance.loginRedirect({
      scopes: FALLBACK_SCOPES,
      loginHint: accountHint,
      redirectStartPage: window.location.href,
    });
  } catch (loginErr) {
    console.error("Login redirect failed", loginErr);
    window.location.href = "/";
  }
};

export const acquireAccessToken = async (
  scopes?: string[]
): Promise<string | null> => {
  try {
    await msalInstanceReady;
  } catch (err) {
    console.error("MSAL failed to initialize", err);
    return getCachedAccessToken();
  }

  const account = msalInstance.getActiveAccount();
  if (!account) {
    return getCachedAccessToken();
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      account,
      scopes: buildRequestScopes(scopes),
    });
    if (response?.accessToken) {
      setCachedAccessToken(response.accessToken);
      return response.accessToken;
    }
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // Refresh token expired (~24 hours) - trigger seamless re-login
      console.log("Refresh token expired - triggering auto re-login");
      await forceRelogin("InteractionRequiredAuthError", account.username);
      return null;
    }
    // Any other error - also force re-login
    console.warn("Token acquisition failed with unexpected error", err);
    await forceRelogin("unexpected error", account.username);
    return null;
  }

  return getCachedAccessToken();
};
